/* eslint-disable no-param-reassign */
const config = require('lib/config')

// create window.console stubs if window.console doesn't exist
const console = (window.console) ? window.console : {}

// detect IE to disable console styling
const isIE = /(MSIE|Trident|Edge)/.test(navigator.userAgent)
const useCss = ! isIE

// define noop function for console stubs
function noop() {}

// Ensure all the console methods we use are always available, even as a passthrough
const console_methods = [
    'log',
    'info',
    'warn',
    'error',
    'time',
    'timeEnd',
    'group',
    'groupCollapsed',
    'groupEnd',
    'profile',
    'profileEnd',
    'assert',
    'dir',
    'clear',
]
console_methods.forEach((method) => {
     
    console[method] = console[method] || noop
})

// Creates the logger instance
function makeLogger(type, maker, create) {
    const logger = maker('log')
    logger.clear = maker('clear')
    logger.info = maker('info')
    logger.warn = maker('warn')
    logger.error = maker('error')
    logger.important = maker('important')
    logger.time = maker('time')
    logger.timeEnd = maker('timeEnd')
    logger.group = maker('group')
    logger.groupCollapsed = maker('groupCollapsed')
    logger.groupEnd = console.groupEnd.bind(console)  
    logger.create = create
    return logger
}

// Return an automatically generated hex value from `name`
function getColor(name) {
    // This method for mapping a name to a color, consistently, is
    // borrowed from http://stackoverflow.com/a/17880920
    // Updated to hash the string, since the above fails hard for long
    // string values, borrowed from http://jsperf.com/hashcodelordvlad
    let hex = 0
    if (name.length) {
        const l = name.length
        for (let i = 0; i < l; i++) {
            const chr = name.charCodeAt(i)
            hex = ((hex << 5) - hex) + chr
            hex |= 0 // Convert to 32bit integer
        }
        hex = hex >>> 0 // Convert to unsigned 32bit integer
    }
    hex = (hex * hex * 1000).toString().replace(/^0/, '')
    hex = `0.${hex}`
    hex = parseFloat(hex)
    hex = Math.floor(hex * 16777215).toString(16)
    return `#${hex}`
}

// Return 'black' or 'white' depending on which has the best contrast
function getText(hex) {
    // This method of deciding whether text should be black or white is
    // borrowed from http://24ways.org/2010/calculating-color-contrast/
    // Remove leading #
    hex = hex.slice(1)
    // Break into RGB values and do the YIG calculation
    hex = ((parseInt(hex.substr(0, 2), 16) * 299) +
            (parseInt(hex.substr(2, 2), 16) * 587) +
            (parseInt(hex.substr(4, 2), 16) * 114)) / 1000
    return (hex >= 128) ? 'black' : 'white'
}

// Return an appropriate CSS string for a label
function makeCss(label) {
    const shape = 'padding:1px 5px 1px 4px;border-radius: 2px;'
    if (label.css) return shape + label.css
    return `${shape}background-color:${label.color};color:${label.text}`
}

// Parse log labels
 
function parseLabels(...args) {
    const prefix = []

    // If no log name was passed, we use the default logger
    if (! args.length) args.push('log')

    let label
    let part
    const labels = []

    while (args.length) {
        part = args.shift()
        // If it's an empty string, or not a string, just ignore it
        if (! part || typeof part !== 'string') continue

        // If the string starts with a #, it's a hex color, which only
        // matters if we already have a label
        if (part[0] === '#') {
            if (! label || ! useCss) continue
            // We use the reference to the label we just pushed, yey
            label.color = part
            label.text = getText(label.color)
            continue
        }

        // If it looks like a CSS string, we want to save it
        if (part.match(/background-color:/) || part.match(/color:/)) {
            if (! label || ! useCss) continue
            label.css = part
            continue
        }

        // Create a new label
        label = { name: part }
        label.color = getColor(part)
        label.text = getText(label.color)
        labels.push(label)
    }

    // Build out our format string and prefix arguments with CSS
    let format = ''
    labels.forEach((item) => {
        if (useCss) {
            format += `%c${item.name}%c `
            prefix.push(makeCss(item))
            prefix.push('background-color:none')
        } else {
            format += `${item.name}:`
        }
    })

    // Strip trailing whitespace
    format = format.replace(/ $/, '')

    // Put the format string as the first argument
    prefix.unshift(format)
    return prefix
}
 

// Creates a new log, huzzah!
function createLog(type) {
    const prefix = parseLabels.apply(null, arguments)

    // Factory function for making log methods
    function maker(method) {
        return function logMethod() {
            // Don't log if js_debug isn't enabled or it's not configured in localStorage
            const js_debug = config.get('pumpkinhead.globals.js_debug', false)
            let log = null
            try {
                log = localStorage && localStorage.getItem('log')
            } catch {
                // console.log('localStorage not available')
            }
            const channels = log && log.split(',').map(s => s.trim())
            const allowed_channels = channels && channels.filter(channel => !channel.startsWith('-')) || []
            const forbidden_channels = channels && channels
                .filter(channel => channel.startsWith('-'))
                .map(channel => channel.substring(1)) || []
            if (!js_debug && !allowed_channels.length) return

            // Filter logging output if localStorage.log defines the logs to show.
            // The format is either '*' for everything or a comma-seperated list like
            // 'onboarding, listeners, radio'. Only the top level category is supported
            // so something like 'radio metrics_editpanel' would not work.
            // Prefix a category with '-' (like '*,-radio') to not display it.
            if (allowed_channels.length) {
                const channel = prefix[0].split('%c %c').map(p => p.replace(/%c/g, ''))[0]
                if (forbidden_channels.includes(channel)) return
                if (!allowed_channels.includes('*') && !allowed_channels.includes(channel)) return
            }

            const args = Array.prototype.slice.call(arguments, 0)
            const logger = console[method] ? console[method] : console.log  

            // The timing methods don't play nice with CSS
            if (method === 'time' || method === 'timeEnd') {
                args.unshift(prefix[0].replace(/%c/g, ''))
            } else {
                args.unshift.apply(args, prefix)
            }

            logger.apply(console, args)
        }
    }
    return makeLogger(type, maker, createLog)
}

const log = createLog('log', 'background-color: grey; color: white')

module.exports = log
